// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import type { ICommand, IErrorResult, IResult, ISuccessResult } from '@ms/utilities-cross-window';
import type { ITokenRetriever, TokenType } from '../../types/external';

// Must stay in sync with odsp-next/app-cores/odsp/src/models/embed/Commands.ts
// Not referenced directly to avoid the additional dependency
type OdspTokenNames = 'SHAREPOINT_TOKEN';
export interface ITokenCommand {
  command: 'getToken';
  tokenType: string;
  forceRefresh: boolean;
  options: {
    tokenType: string;
    forceRefresh: boolean;
  };
}

interface ITokenSuccessResult extends ISuccessResult {
  token: string;
  expires?: number;
}

type TokenNameMapType = {
  readonly [k in OdspTokenNames]: TokenType;
};
const TokenNameMap: TokenNameMapType = {
  ['SHAREPOINT_TOKEN']: 'SPO'
};

export async function onGetTokenCommand(
  command: ICommand,
  tokenRetriever: ITokenRetriever,
  embedUrl: string
): Promise<IResult> {
  if (Object.prototype.hasOwnProperty.call(TokenNameMap, (command as ITokenCommand).tokenType)) {
    const tokenType = (command as ITokenCommand).tokenType as OdspTokenNames;
    try {
      const authToken = await tokenRetriever.getTokenAsync(TokenNameMap[tokenType], embedUrl);
      const result: ITokenSuccessResult = {
        result: 'success',
        token: authToken
      };
      return result;
    } catch (err: unknown) {
      let code: string = '';
      let message: string = '';

      if (typeof err === 'object' && err !== null) {
        const errorLike: {
          code?: string;
          message?: string;
        } = err;
        code =
          Object.prototype.hasOwnProperty.call(errorLike, 'code') && typeof errorLike.code === 'string'
            ? errorLike.code
            : '';
        message =
          Object.prototype.hasOwnProperty.call(errorLike, 'message') && typeof errorLike.message === 'string'
            ? errorLike.message
            : '';
      }

      const result: IErrorResult = {
        result: 'error',
        isExpected: false,
        error: {
          code,
          message
        }
      };
      return result;
    }
  }

  const result: IErrorResult = {
    result: 'error',
    isExpected: false,
    error: {
      code: 'UnsupportedTokenType',
      message: 'Unsupported token type'
    }
  };
  return result;
}
